import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';
import { Payout, ApiResponse } from '../types';
import clsx from 'clsx';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  processing: 'bg-indigo-100 text-indigo-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]',
};

export default function Payouts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const page = parseInt(searchParams.get('page') || '1');
  const status = searchParams.get('status') || '';

  const { data, isLoading } = useQuery({
    queryKey: ['payouts', { status, page }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      params.set('page', page.toString());

      const response = await api.get<ApiResponse<Payout[]>>(`/payouts?${params}`);
      return response.data;
    },
  });

  const processPayout = useMutation({
    mutationFn: async ({ id, status, notes }: { id: string; status: string; notes?: string }) => {
      await api.patch(`/payouts/${id}/process`, { status, notes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payouts'] });
      toast.success('Payout updated');
    },
    onError: () => {
      toast.error('Failed to update payout');
    },
  });

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Payouts</h1>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            Manage affiliate payout requests.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6">
        <select
          value={status}
          onChange={(e) => {
            const params = new URLSearchParams(searchParams);
            if (e.target.value) {
              params.set('status', e.target.value);
            } else {
              params.delete('status');
            }
            params.set('page', '1');
            setSearchParams(params);
          }}
          className="input max-w-xs"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Table */}
      <div className="mt-6 overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-[var(--bg-secondary)]">
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-[var(--text-primary)]">
                Affiliate
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Amount
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Method
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Status
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Requested
              </th>
              <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-[var(--bg-secondary)]">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="py-10 text-center text-[var(--text-muted)]">
                  Loading...
                </td>
              </tr>
            ) : data?.data?.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-10 text-center text-[var(--text-muted)]">
                  No payouts found
                </td>
              </tr>
            ) : (
              data?.data?.map((payout) => (
                <tr key={payout.id}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm">
                    <div className="font-medium text-[var(--text-primary)]">{payout.affiliate.name}</div>
                    <div className="text-[var(--text-muted)]">{payout.affiliate.email}</div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-[var(--text-primary)]">
                    ${payout.amount.toLocaleString()} {payout.currency}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-muted)] capitalize">
                    {payout.paymentMethod}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <span
                      className={clsx(
                        'badge',
                        statusColors[payout.status as keyof typeof statusColors]
                      )}
                    >
                      {payout.status}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-muted)]">
                    {new Date(payout.createdAt).toLocaleDateString()}
                  </td>
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    {payout.status === 'pending' && (
                      <>
                        <button
                          onClick={() =>
                            processPayout.mutate({ id: payout.id, status: 'approved' })
                          }
                          className="text-green-600 hover:text-green-900 mr-3"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() =>
                            processPayout.mutate({
                              id: payout.id,
                              status: 'rejected',
                              notes: 'Rejected by admin',
                            })
                          }
                          className="text-red-600 hover:text-red-900"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {payout.status === 'approved' && (
                      <button
                        onClick={() =>
                          processPayout.mutate({ id: payout.id, status: 'processing' })
                        }
                        className="text-primary-600 hover:text-primary-900"
                      >
                        Process
                      </button>
                    )}
                    {payout.status === 'processing' && (
                      <button
                        onClick={() =>
                          processPayout.mutate({ id: payout.id, status: 'completed' })
                        }
                        className="text-green-600 hover:text-green-900"
                      >
                        Mark Complete
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data?.pagination && data.pagination.pages > 1 && (
        <div className="mt-4 flex justify-between items-center">
          <p className="text-sm text-[var(--text-muted)]">
            Showing {((page - 1) * data.pagination.limit) + 1} to{' '}
            {Math.min(page * data.pagination.limit, data.pagination.total)} of{' '}
            {data.pagination.total} results
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set('page', (page - 1).toString());
                setSearchParams(params);
              }}
              disabled={page === 1}
              className="btn btn-secondary"
            >
              Previous
            </button>
            <button
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set('page', (page + 1).toString());
                setSearchParams(params);
              }}
              disabled={page >= data.pagination.pages}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
